(function () {
  'use strict';

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  function announce(message) {
    const region = $('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 40);
  }

  function formatMoney(cents, format) {
    const value = Number(cents || 0);
    const withDelimiters = (amount, precision = 2) => {
      const parts = (amount / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    };
    const template = format || '₹{{amount}}';
    return template
      .replace('{{amount_no_decimals}}', withDelimiters(value, 0))
      .replace('{{amount_with_comma_separator}}', withDelimiters(value, 2).replace(',', 'X').replace('.', ',').replace('X', '.'))
      .replace('{{amount}}', withDelimiters(value, 2));
  }

  function initialisePageExperience() {
    const body = document.body;
    const progress = $('[data-scroll-progress]');
    const header = $('[data-site-header]');
    let ticking = false;

    const updateScrollState = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      if (progress) progress.style.transform = `scaleX(${Math.min(1, scrollTop / scrollable)})`;
      if (header) header.classList.toggle('is-condensed', scrollTop > 28);
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateScrollState);
    }, { passive: true });
    updateScrollState();

    window.requestAnimationFrame(() => window.requestAnimationFrame(() => body.classList.add('gh-page-ready')));
    window.addEventListener('pageshow', () => body.classList.remove('gh-page-leaving'));

    if (!body.classList.contains('gh-transitions')) return;
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target === '_blank' || link.hasAttribute('download') || link.dataset.noTransition !== undefined) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.protocol === 'mailto:' || url.protocol === 'tel:') return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      event.preventDefault();
      body.classList.add('gh-page-leaving');
      window.setTimeout(() => { window.location.href = url.href; }, 220);
    });
  }

  function initialiseMobileMenu() {
    const toggle = $('[data-menu-toggle]');
    const menu = $('[data-mobile-menu]');
    if (!toggle || !menu) return;

    const close = () => {
      toggle.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
      document.body.classList.remove('gh-menu-open');
    };

    toggle.addEventListener('click', () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isOpen));
      menu.hidden = isOpen;
      document.body.classList.toggle('gh-menu-open', !isOpen);
      if (!isOpen) $('a', menu)?.focus();
    });

    $$('a', menu).forEach((link) => link.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !menu.hidden) {
        close();
        toggle.focus();
      }
    });
  }

  function initialiseWishlist() {
    const storageKey = 'grahhasthi-wishlist';
    let saved = [];
    try {
      saved = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    } catch (_error) {
      saved = [];
    }

    $$('[data-wishlist]').forEach((button) => {
      const handle = button.dataset.wishlist;
      const update = () => {
        const active = saved.includes(handle);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', active ? 'Remove from saved objects' : 'Save this object');
      };

      update();
      button.addEventListener('click', () => {
        saved = saved.includes(handle) ? saved.filter((item) => item !== handle) : [...saved, handle];
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(saved));
        } catch (_error) {
          // The visual state still works when browser storage is unavailable.
        }
        update();
        announce(saved.includes(handle) ? 'Object saved.' : 'Object removed from saved items.');
      });
    });
  }

  function initialiseQuantityControls() {
    $$('[data-quantity-minus], [data-quantity-plus]').forEach((button) => {
      button.addEventListener('click', () => {
        const control = button.closest('.gh-quantity');
        const input = $('[data-quantity-input]', control);
        if (!input) return;
        const minimum = Number(input.min || 0);
        const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const direction = button.hasAttribute('data-quantity-plus') ? 1 : -1;
        const next = Math.min(maximum, Math.max(minimum, Number(input.value || minimum) + direction));
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function initialiseProductGallery() {
    $$('[data-product-gallery]').forEach((gallery) => {
      $$('[data-gallery-thumb]', gallery).forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const mediaId = thumb.dataset.galleryThumb;
          $$('[data-product-media]', gallery).forEach((media) => {
            media.classList.toggle('is-hidden', media.dataset.productMedia !== mediaId);
          });
          $$('[data-gallery-thumb]', gallery).forEach((item) => {
            const active = item === thumb;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
        });
      });
    });
  }

  function initialiseProductForms() {
    $$('[data-product-section]').forEach((section) => {
      const select = $('[data-variant-select]', section);
      const price = $('[data-product-price]', section);
      const button = $('[data-add-to-cart]', section);
      const buttonText = $('[data-add-to-cart-text]', section);

      if (select) {
        select.addEventListener('change', () => {
          const option = select.options[select.selectedIndex];
          const available = option.dataset.available === 'true';
          const compareAt = option.dataset.compare;
          if (price) {
            price.innerHTML = compareAt && compareAt !== option.dataset.price
              ? `<s>${compareAt}</s> <span>${option.dataset.price}</span> <small>Tax included.</small>`
              : `<span>${option.dataset.price}</span> <small>Tax included.</small>`;
          }
          if (button) button.disabled = !available;
          if (buttonText) buttonText.textContent = available ? 'Add to bag' : 'Sold out';

          const nextUrl = new URL(window.location.href);
          nextUrl.searchParams.set('variant', option.value);
          window.history.replaceState({}, '', nextUrl.toString());
        });
      }

      const giftToggle = $('[data-gift-toggle]', section);
      const giftDetails = $('[data-gift-details]', section);
      if (giftToggle && giftDetails) {
        giftToggle.addEventListener('change', () => {
          giftDetails.hidden = !giftToggle.checked;
          const message = $('textarea', giftDetails);
          if (!giftToggle.checked && message) message.value = '';
        });
      }

      const form = $('.gh-product-form', section);
      if (form && !form.dataset.ajaxReady) {
        form.dataset.ajaxReady = 'true';
        form.addEventListener('submit', async (event) => {
          if (event.submitter && event.submitter.name !== 'add') return;
          event.preventDefault();
          const submitButton = $('[data-add-to-cart]', form);
          const submitText = $('[data-add-to-cart-text]', form);
          const originalText = submitText?.textContent || 'Add to bag';
          if (submitButton) submitButton.disabled = true;
          if (submitText) submitText.textContent = 'Adding…';

          try {
            const response = await fetch(`${window.GrahhasthiTheme.routes.cartAdd}.js`, {
              method: 'POST',
              headers: { Accept: 'application/json' },
              body: new FormData(form)
            });
            const item = await response.json();
            if (!response.ok) throw new Error(item.description || 'This object could not be added.');
            const cartResponse = await fetch(`${window.GrahhasthiTheme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
            const cart = await cartResponse.json();
            $$('[data-cart-count]').forEach((count) => { count.textContent = String(cart.item_count); });
            const toast = $('[data-cart-toast]');
            if (toast) {
              $('[data-cart-toast-title]', toast).textContent = item.product_title || item.title;
              $('[data-cart-toast-total]', toast).textContent = formatMoney(cart.total_price, window.GrahhasthiTheme.moneyFormat);
              toast.hidden = false;
              window.requestAnimationFrame(() => toast.classList.add('is-visible'));
              window.setTimeout(() => toast.classList.remove('is-visible'), 6500);
            }
            if (submitText) submitText.textContent = 'Added to bag ✓';
            announce(`${item.product_title || item.title} added to your bag.`);
          } catch (error) {
            if (submitText) submitText.textContent = 'Please try again';
            announce(error.message);
          } finally {
            window.setTimeout(() => {
              if (submitButton) submitButton.disabled = false;
              if (submitText) submitText.textContent = originalText;
            }, 1400);
          }
        });
      }
    });
  }

  function initialiseCartToast() {
    const toast = $('[data-cart-toast]');
    const close = $('[data-cart-toast-close]', toast || document);
    if (!toast || !close) return;
    close.addEventListener('click', () => toast.classList.remove('is-visible'));
  }

  function initialiseCollectionControls() {
    $$('[data-sort-select]').forEach((select) => {
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  function initialiseDeliveryPreferences() {
    $$('[data-delivery-preferences]').forEach((fieldset) => {
      const schedule = $('[data-schedule-fields]', fieldset);
      const pickup = $('[data-pickup-note]', fieldset);
      const dateInput = schedule ? $('input[type="date"]', schedule) : null;
      const radios = $$('input[type="radio"]', fieldset);

      const refresh = () => {
        const selected = radios.find((radio) => radio.checked)?.value || '';
        const scheduled = selected === 'Scheduled Delhi NCR';
        const collection = selected === 'Store pickup';
        if (schedule) schedule.hidden = !scheduled;
        if (pickup) pickup.hidden = !collection;
        if (dateInput) dateInput.required = scheduled;
      };

      radios.forEach((radio) => radio.addEventListener('change', refresh));
      refresh();
    });
  }

  function initialisePincodeCheckers() {
    $$('[data-pincode-checker]').forEach((checker) => {
      const controls = $('[data-pincode-form]', checker);
      const input = $('[data-pincode-input]', checker);
      const submit = $('[data-pincode-submit]', checker);
      const result = $('[data-pincode-result]', checker);
      const standardCopy = $('[data-standard-delivery-copy]', checker)?.textContent.trim() || '';
      const scheduledCopy = $('[data-scheduled-delivery-copy]', checker)?.textContent.trim() || '';
      const scheduledPrefixes = (checker.dataset.scheduledPrefixes || '')
        .split(',')
        .map((prefix) => prefix.trim())
        .filter(Boolean);

      if (!controls || !input || !submit || !result) return;

      input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 6);
        result.classList.remove('is-success', 'is-error');
      });

      const checkPincode = () => {
        if (checker.dataset.productAvailable === 'false') {
          result.textContent = 'This object is currently unavailable. Delivery and pickup can be checked when it is back in stock.';
          result.classList.remove('is-success');
          result.classList.add('is-error');
          return;
        }
        const pincode = input.value.trim();
        if (!/^[1-9][0-9]{5}$/.test(pincode)) {
          result.textContent = 'Enter a valid six-digit Indian pincode.';
          result.classList.remove('is-success');
          result.classList.add('is-error');
          input.focus();
          return;
        }

        const isScheduled = scheduledPrefixes.some((prefix) => pincode.startsWith(prefix));
        result.textContent = isScheduled ? scheduledCopy : standardCopy;
        result.classList.remove('is-error');
        result.classList.add('is-success');
        announce(isScheduled ? 'Scheduled delivery information shown.' : 'Standard delivery information shown.');
      };

      submit.addEventListener('click', checkPincode);
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        event.stopPropagation();
        checkPincode();
      });
    });
  }

  function initialiseAccordions() {
    $$('[data-accordion-group]').forEach((group) => {
      $$('details', group).forEach((details) => {
        details.addEventListener('toggle', () => {
          const mark = $('summary span', details);
          if (mark) mark.textContent = details.open ? '−' : '+';
        });
      });
    });
  }

  function initialiseReveal() {
    const autoRevealSelectors = [
      '.gh-section-head', '.gh-manifesto > *', '.gh-product-card', '.gh-journal article',
      '.gh-pillars article', '.gh-gifting-split__content > *', '.gh-editorial-card__content > *',
      '.gh-occasion-guide__list > a', '.gh-product__buy > *', '.gh-cart-item',
      '.gh-basic-page > *', '.gh-footer__grid > *'
    ];
    $$(autoRevealSelectors.join(',')).forEach((item, index) => {
      item.classList.add('gh-reveal');
      item.style.setProperty('--gh-delay', `${Math.min(index % 4, 3) * 80}ms`);
    });
    const items = $$('.gh-reveal');
    if (!items.length || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach((item) => observer.observe(item));
  }

  function initialiseDepthMotion() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !window.matchMedia('(pointer: fine)').matches) return;
    $$('[data-product-card], .gh-editorial-card').forEach((card) => {
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--gh-x', `${((event.clientX - rect.left) / rect.width - .5) * 8}px`);
        card.style.setProperty('--gh-y', `${((event.clientY - rect.top) / rect.height - .5) * 8}px`);
      });
      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--gh-x', '0px');
        card.style.setProperty('--gh-y', '0px');
      });
    });

    const heroMedia = $('.gh-hero__media');
    if (heroMedia) {
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking || window.scrollY > window.innerHeight * 1.3) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          heroMedia.style.transform = `translate3d(0, ${Math.min(window.scrollY * .12, 70)}px, 0) scale(1.035)`;
          ticking = false;
        });
      }, { passive: true });
    }
  }

  function initialiseHeroSliders() {
    $$('[data-hero-slider]').forEach((slider) => {
      const slides = $$('[data-hero-slide]', slider);
      if (slides.length < 2) return;
      const hero = slider.closest('.gh-hero') || slider;
      const previous = $('[data-hero-previous]', hero);
      const next = $('[data-hero-next]', hero);
      const current = $('[data-hero-current]', hero);
      const total = $('[data-hero-total]', hero);
      const autoplay = slider.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const delay = Math.max(3500, Number(slider.dataset.delay || 6500));
      let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer;

      const show = (index, announceChange = false) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const selected = slideIndex === active;
          slide.classList.toggle('is-active', selected);
          slide.setAttribute('aria-hidden', String(!selected));
        });
        if (current) current.textContent = String(active + 1).padStart(2, '0');
        if (total) total.textContent = String(slides.length).padStart(2, '0');
        if (announceChange) announce(`Showing banner ${active + 1} of ${slides.length}.`);
      };

      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (autoplay) timer = window.setInterval(() => show(active + 1), delay);
      };

      previous?.addEventListener('click', () => { show(active - 1, true); start(); });
      next?.addEventListener('click', () => { show(active + 1, true); start(); });
      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('focusin', stop);
      slider.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
      document.addEventListener('shopify:block:select', (event) => {
        const selected = slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
        if (selected >= 0) { stop(); show(selected); }
      });
      show(active);
      start();
    });
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');
          if (recommendations && recommendations.innerHTML.trim()) {
            this.innerHTML = recommendations.innerHTML;
            initialiseWishlist();
            initialiseReveal();
          }
        })
        .catch(() => {
          // Recommendation failures should never interrupt the product page.
        });
    }
  }

  if (!window.customElements.get('product-recommendations')) {
    window.customElements.define('product-recommendations', ProductRecommendations);
  }

  function initialise() {
    initialisePageExperience();
    initialiseMobileMenu();
    initialiseWishlist();
    initialiseQuantityControls();
    initialiseProductGallery();
    initialiseProductForms();
    initialiseCollectionControls();
    initialiseDeliveryPreferences();
    initialisePincodeCheckers();
    initialiseAccordions();
    initialiseReveal();
    initialiseDepthMotion();
    initialiseHeroSliders();
    initialiseCartToast();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
